import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Grid, Card, CardContent,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import {
  ADMIN_ALL_PARKING_QUERY,
  ADMIN_ALL_CEREMONIES_QUERY,
  CREATE_PARKING_SLOT_MUTATION,
  UPDATE_PARKING_SLOT_MUTATION,
  DELETE_PARKING_SLOT_MUTATION,
} from '../graphql/queries';
import ContractedVendorsSection from '../components/ContractedVendorsSection';
import { useFamily } from '../context/FamilyContext';
import { ownerName, actAsOwner } from '../utils/familyOwner';

const STATUS_COLOR = { available: 'success', occupied: 'error', reserved: 'warning', maintenance: 'default' };
const SLOT_TYPES   = ['standard','vip','handicap','two_wheeler','valet'];

const EMPTY = { ceremonyId: '', zoneName: '', slotNumber: '', slotType: 'standard', notes: '' };

export default function AdminParkingPage() {
  const [dialog, setDialog]   = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [snack, setSnack]     = useState({ open: false, msg: '', ok: true });

  const { family } = useFamily();
  const { data, loading, refetch } = useQuery(ADMIN_ALL_PARKING_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: ceremonyData } = useQuery(ADMIN_ALL_CEREMONIES_QUERY, { skip: !family, fetchPolicy: 'cache-and-network' });
  const ceremonies = ceremonyData?.allCeremonies || [];

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [create, { loading: creating }] = useMutation(CREATE_PARKING_SLOT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Slot added.'); },
    onError: e => toast(e.message, false),
  });
  const [update, { loading: updating }] = useMutation(UPDATE_PARKING_SLOT_MUTATION, {
    onCompleted: () => { closeDialog(); refetch(); toast('Slot updated.'); },
    onError: e => toast(e.message, false),
  });
  const [remove] = useMutation(DELETE_PARKING_SLOT_MUTATION, {
    onCompleted: () => { refetch(); toast('Slot removed.'); },
    onError: e => toast(e.message, false),
  });

  const slots = data?.allParkingSlots || [];

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ceremonyId: s.ceremonyId || '', zoneName: s.zoneName, slotNumber: s.slotNumber, slotType: s.slotType, notes: s.notes || '' });
    setDialog(true);
  };
  const closeDialog = () => { setDialog(false); setEditing(null); setForm(EMPTY); };

  const handleSave = () => {
    if (!form.slotNumber.trim()) return;
    const { ceremonyId, ...fields } = form;
    if (editing) update({ variables: { id: editing.id, ...fields }, ...actAsOwner(editing.owner) });
    else create({ variables: { ...fields, ceremonyId: ceremonyId || null } });
  };

  const stats = {
    total: slots.length,
    available: slots.filter(s => s.status === 'available').length,
    occupied: slots.filter(s => s.status === 'occupied').length,
    reserved: slots.filter(s => s.status === 'reserved').length,
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Parking Management</Typography>
          <Typography variant="body2" color="text.secondary">
            {family ? `${family.displayName}'s parking slots` : 'Parking slots of all bride/groom families'}
          </Typography>
        </Box>
        <Tooltip title={family ? '' : 'Select a bride/groom family above to add a slot for them'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!family} onClick={() => { setEditing(null); setForm(EMPTY); setDialog(true); }}>
              Add Slot
            </Button>
          </span>
        </Tooltip>
      </Stack>

      <ContractedVendorsSection category="parking" />

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Slots', value: stats.total, color: 'primary' },
          { label: 'Available', value: stats.available, color: 'success' },
          { label: 'Occupied', value: stats.occupied, color: 'error' },
          { label: 'Reserved', value: stats.reserved, color: 'warning' },
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center', py: 1 }}>
                <LocalParkingIcon color={s.color} />
                <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
                <Typography variant="caption">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              {!family && <TableCell>Family</TableCell>}
              <TableCell>Zone</TableCell>
              <TableCell>Slot No.</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Ceremony</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map(s => (
              <TableRow key={s.id} hover>
                {!family && <TableCell>{ownerName(s.owner)}</TableCell>}
                <TableCell>{s.zoneName || '—'}</TableCell>
                <TableCell><strong>{s.slotNumber}</strong></TableCell>
                <TableCell>{s.slotType}</TableCell>
                <TableCell>{s.ceremony?.name || '—'}</TableCell>
                <TableCell><Chip label={s.status} size="small" color={STATUS_COLOR[s.status] || 'default'} /></TableCell>
                <TableCell>{s.notes || '—'}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(s)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => remove({ variables: { id: s.id }, ...actAsOwner(s.owner) })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {slots.length === 0 && (
              <TableRow><TableCell colSpan={family ? 7 : 8} align="center">{family ? `No parking slots for ${family.displayName} yet.` : 'No parking slots configured.'}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={dialog} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? `Edit Parking Slot — ${ownerName(editing.owner)}` : `Add Parking Slot for ${family?.displayName || ''}`}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {!editing && (
              <TextField select label="Ceremony (optional)" value={form.ceremonyId} onChange={e => setForm(f => ({ ...f, ceremonyId: e.target.value }))} fullWidth>
                <MenuItem value=""><em>Not linked to a ceremony</em></MenuItem>
                {ceremonies.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
            )}
            <TextField label="Zone Name" value={form.zoneName} onChange={e => setForm(f => ({ ...f, zoneName: e.target.value }))} fullWidth />
            <TextField label="Slot Number" value={form.slotNumber} onChange={e => setForm(f => ({ ...f, slotNumber: e.target.value }))} required fullWidth />
            <TextField select label="Type" value={form.slotType} onChange={e => setForm(f => ({ ...f, slotType: e.target.value }))} fullWidth>
              {SLOT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} multiline rows={2} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={creating || updating} startIcon={<SaveIcon />}>
            {editing ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
